import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';

type Status = 'checking' | 'ready' | 'invalid' | 'done';

function validatePassword(password: string): string | null {
  if (password.length < 8) return '비밀번호는 8자 이상이어야 합니다.';
  if (!/[a-zA-Z]/.test(password)) return '비밀번호에 영문을 포함해주세요.';
  if (!/[0-9]/.test(password)) return '비밀번호에 숫자를 포함해주세요.';
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return '비밀번호에 특수문자를 포함해주세요.';
  return null;
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('checking');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let settled = false;

    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error') || params.get('error_description');
    const code = params.get('code');

    if (errorParam) {
      settled = true;
      setStatus('invalid');
      return;
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        settled = true;
        setStatus('ready');
      }
    });

    // PKCE 플로우: URL 쿼리에 ?code=... 형태로 전달되는 경우 직접 세션 교환
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ data, error }) => {
        if (!settled) {
          if (data.session && !error) {
            settled = true;
            setStatus('ready');
          } else if (error) {
            console.error('Exchange code for session error:', error);
          }
        }
      });
    }

    // implicit 플로우: 이미 세션이 처리된 뒤(리스너 등록 전) 마운트된 경우를 대비한 폴백 체크
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!settled && session) {
        settled = true;
        setStatus('ready');
      }
    });

    // 링크가 만료됐거나 잘못된 경우를 대비한 타임아웃
    const timeout = setTimeout(() => {
      if (!settled) {
        setStatus('invalid');
      }
    }, 4000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password || !passwordConfirm) {
      setError('새 비밀번호를 입력해주세요.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message || '비밀번호 변경에 실패했습니다.');
        return;
      }
      await supabase.auth.signOut();
      setStatus('done');
    } catch (err) {
      console.error('Reset password error:', err);
      setError('비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-soft p-8">
            {status === 'checking' && (
              <div className="text-center py-4">
                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                <p className="text-gray-600">인증 링크를 확인하고 있습니다...</p>
              </div>
            )}

            {status === 'invalid' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">유효하지 않은 링크입니다</h1>
                <p className="text-gray-500 text-sm mb-6">
                  비밀번호 재설정 링크가 만료되었거나 이미 사용되었습니다.
                  <br />
                  비밀번호 찾기를 다시 시도해주세요.
                </p>
                <Link
                  to="/forgot-password"
                  className="block w-full font-semibold px-6 py-3 rounded-lg bg-primary hover:bg-primary-600 text-white transition-colors"
                >
                  비밀번호 찾기로 이동
                </Link>
              </div>
            )}

            {status === 'ready' && (
              <>
                <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">새 비밀번호 설정</h1>
                <p className="text-gray-500 text-center text-sm mb-8">
                  새로 사용하실 비밀번호를 입력해주세요.
                </p>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm text-center">{error}</p>
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="영문·숫자·특수문자 포함 8자 이상"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호 확인</label>
                    <input
                      type="password"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full font-semibold px-6 py-3 rounded-lg transition-colors ${
                      submitting
                        ? 'bg-gray-400 cursor-not-allowed text-white'
                        : 'bg-primary hover:bg-primary-600 text-white'
                    }`}
                  >
                    {submitting ? '변경 중...' : '비밀번호 변경'}
                  </button>
                </form>
              </>
            )}

            {status === 'done' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">비밀번호가 변경되었습니다</h1>
                <p className="text-gray-500 text-sm mb-6">
                  새 비밀번호로 다시 로그인해주세요.
                </p>
                <Link
                  to="/login"
                  className="block w-full font-semibold px-6 py-3 rounded-lg bg-primary hover:bg-primary-600 text-white transition-colors"
                >
                  로그인하러 가기
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
